// AFO collection controls (collection-page feature).
// Custom element handling: lights on/off state (sessionStorage persisted),
// re-render self-heal, and (later steps) view switcher + sticky bar sync.
// Registered on the results-list page; no globals.

const LIGHTS_KEY = 'afo-lights-on';
const VIEW_KEY = 'afo-collection-view';
const VIEWS = ['s', 'm', 'l'];

if (!customElements.get('afo-collection-controls')) {
  customElements.define(
    'afo-collection-controls',
    class AfoCollectionControls extends HTMLElement {
      connectedCallback() {
        this.applyStoredState();
        this.disableFacetOverflow();
        this.restoreFilterPanel();
        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        // Section re-render (results-list morph) rebuilds/patches inputs unchecked without
        // reconnecting this element; property patches don't hit MutationObserver on the input
        // itself, so we watch the whole results-list for re-render churn and re-sync after it
        // (logo-morph self-heal pattern).
        this.observer = new MutationObserver(() => queueMicrotask(() => this.syncInputs()));
        this.observer.observe(this.closest('results-list') || this, { childList: true, subtree: true });
        this.setupStickyBar();
        // sidebar lives outside this element — delegate its accordion clicks at document level
        this.onDocClick = (event) => {
          const btn = event.target.closest('[data-afo-group-toggle]');
          if (!btn) return;
          const li = btn.closest('.afo-col-sidebar__group');
          const willOpen = !li.classList.contains('is-open');
          li.parentElement.querySelectorAll(':scope > .afo-col-sidebar__group.is-open').forEach((g) => {
            g.classList.remove('is-open');
            g.querySelector('[data-afo-group-toggle]')?.setAttribute('aria-expanded', 'false');
          });
          if (willOpen) {
            li.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
          }
        };
        document.addEventListener('click', this.onDocClick);
      }

      // Sticky utility bar: appears when the main title bar scrolls out of view,
      // pinned below the (always-sticky) AFO header — offset is measured, not assumed.
      setupStickyBar() {
        const titlebar = this.querySelector('.afo-col-titlebar');
        const stickybar = this.querySelector('[data-afo-stickybar]');
        if (!titlebar || !stickybar) return;
        this.measureStickyTop();
        this.intersection = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            if (show) this.measureStickyTop();
            document.documentElement.classList.toggle('afo-stickybar-visible', show);
          },
          { rootMargin: '-90px 0px 0px 0px' },
        );
        this.intersection.observe(titlebar);
        this.onResize = () => this.measureStickyTop();
        window.addEventListener('resize', this.onResize);
      }

      measureStickyTop() {
        let bottom = 0;
        document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => {
          const rect = el.getBoundingClientRect();
          // pinned header stack: announcement + header rows near the viewport top
          if (rect.top >= -5 && rect.top < 150 && rect.bottom > bottom && rect.height > 0) bottom = rect.bottom;
        });
        document.documentElement.style.setProperty('--afo-col-stickybar-top', `${Math.max(0, Math.round(bottom))}px`);
      }

      syncInputs() {
        const on = document.documentElement.classList.contains('afo-lights-on');
        this.querySelectorAll('[data-afo-light-input]').forEach((input) => {
          if (input.checked !== on) input.checked = on;
        });
        const view = sessionStorage.getItem(VIEW_KEY) || 'm';
        this.querySelectorAll('[data-afo-view-input]').forEach((input) => {
          const should = input.value === view;
          if (input.checked !== should) input.checked = should;
        });
      }

      // The facets overflow-list would hide groups behind a "more" control;
      // the ICW panel shows every group as a column, so overflow management is turned off.
      disableFacetOverflow() {
        const scope = this.closest('results-list') || document;
        scope.querySelectorAll('.facets-block-wrapper--horizontal overflow-list').forEach((list) => {
          list.setAttribute('disabled', 'true');
        });
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onClick);
        if (this.observer) this.observer.disconnect();
        if (this.intersection) this.intersection.disconnect();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
      }

      onChange = (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.hasAttribute('data-afo-light-input')) {
          this.setLights(input.checked);
        }
        if (input.hasAttribute('data-afo-view-input') && input.checked) {
          this.setView(input.value);
        }
      };

      onClick = (event) => {
        const macc = event.target.closest('[data-afo-macc]');
        if (macc) {
          const open = !document.documentElement.classList.contains('afo-macc-open');
          document.documentElement.classList.toggle('afo-macc-open', open);
          macc.setAttribute('aria-expanded', open ? 'true' : 'false');
          return;
        }
        const mfilter = event.target.closest('[data-afo-mfilter]');
        if (mfilter) {
          // mobile: delegate to the vendor facets drawer toggle (its own row is hidden)
          const vendorBtn = (this.closest('results-list') || document).querySelector('.facets-toggle__button');
          if (vendorBtn) vendorBtn.click();
          return;
        }
        const trigger = event.target.closest('[data-afo-filter-trigger]');
        if (!trigger) return;
        this.toggleFilterPanel(trigger);
      };

      toggleFilterPanel(trigger) {
        const open = !document.documentElement.classList.contains('afo-filter-open');
        this.setFilterPanel(open, trigger);
      }

      // Filter-open state lives on <html> so it survives the results-list section re-render;
      // on reconnect (fresh DOM after filtering) the panel is restored to the stored state.
      restoreFilterPanel() {
        if (document.documentElement.classList.contains('afo-filter-open')) {
          this.setFilterPanel(true, this.querySelector('[data-afo-filter-trigger]'));
        }
      }

      setFilterPanel(open, trigger) {
        const scope = this.closest('results-list') || document;
        document.documentElement.classList.toggle('afo-filter-open', open);
        this.querySelectorAll('[data-afo-filter-trigger]').forEach((btn) => {
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        // ICW panel shows all facet groups as permanently-open columns.
        // Deferred past the click's bubble phase — the vendor accordion has a document-level
        // outside-click auto-close that would immediately revert a same-cycle open.
        setTimeout(() => {
          scope.querySelectorAll('.facets-block-wrapper--horizontal .facets__filters-wrapper accordion-custom').forEach((acc) => {
            if (open) {
              if (acc.hasAttribute('data-auto-close-details')) {
                acc.setAttribute('data-afo-restore-autoclose', acc.getAttribute('data-auto-close-details'));
                acc.removeAttribute('data-auto-close-details');
              }
            } else if (acc.hasAttribute('data-afo-restore-autoclose')) {
              acc.setAttribute('data-auto-close-details', acc.getAttribute('data-afo-restore-autoclose'));
              acc.removeAttribute('data-afo-restore-autoclose');
            }
            const details = acc.querySelector('details.facets__panel');
            if (details) details.open = open;
          });
        }, 0);
      }

      applyStoredState() {
        const on = sessionStorage.getItem(LIGHTS_KEY) === '1';
        this.setLights(on, { silent: true });
        const view = sessionStorage.getItem(VIEW_KEY);
        if (VIEWS.includes(view)) this.setView(view, { silent: true });
      }

      setView(view, { silent = false } = {}) {
        if (!silent) sessionStorage.setItem(VIEW_KEY, view);
        this.querySelectorAll('[data-afo-view-input]').forEach((input) => {
          input.checked = input.value === view;
        });
        const apply = () => {
          VIEWS.forEach((v) => {
            document.documentElement.classList.toggle(`afo-view-${v}`, v === view && v !== 'm');
          });
        };
        const grid = (this.closest('results-list') || document).querySelector('.product-grid');
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (silent || reduced || !grid) {
          apply();
          return;
        }
        // ICW effect (measured): 0.4s fade-out -> swap columns while invisible -> ~120ms hold -> 0.4s fade-in
        clearTimeout(this.viewFadeTimer);
        grid.classList.add('afo-grid-fade');
        this.viewFadeTimer = setTimeout(() => {
          apply();
          this.viewFadeTimer = setTimeout(() => {
            grid.classList.remove('afo-grid-fade');
          }, 120);
        }, 400);
      }

      setLights(on, { silent = false } = {}) {
        document.documentElement.classList.toggle('afo-lights-on', on);
        if (!silent) sessionStorage.setItem(LIGHTS_KEY, on ? '1' : '0');
        this.querySelectorAll('[data-afo-light-input]').forEach((input) => {
          input.checked = on;
        });
      }
    },
  );
}
